import { cast, LocalDateTime } from "@flockcover/flock-svc-core-types"
import {
  Box,
  Feature,
  Flex,
  getLineStringSpeedMetrics,
  LineString,
  LineStringFeatureProperties,
  Text,
  SpeedMetrics,
} from "@shared/primitive-components"
import { localDateTimeToString } from "@shared/utils/formatters/dates"
import { speedingClassification, SpeedingClassification } from "../../../utils"
import { mapFeatureTooltipStyles as styles } from "../../styles"

const generateAverageSpeedClassLabelOld = (speedingClass: SpeedingClassification, metrics: SpeedMetrics): string => {
  switch (speedingClass) {
    case `summons`:
      return `critical speeding`
    case `penalty`:
      return `major speeding`
    case `speeding`:
      return `minor speeding`
    case `unreliable`:
      return `unreliable speed`
    case `lawful`:
      return metrics.speedMph === null ? `no speeding` : `no speeding`
    default:
      return `-`
  }
}

const generateAverageSpeedClassLabel = (feature: Feature<LineString, LineStringFeatureProperties>): string => {
  switch (feature.properties.speedingCategory) {
    case 3:
      return feature.properties.isUrban === true ? `urban critical speeding` : `critical speeding`
    case 2:
      return feature.properties.isUrban === true ? `urban major speeding` : `major speeding`
    case 1:
      return feature.properties.isUrban === true ? `urban minor speeding` : `minor speeding`
    case 4:
      return `unreliable speed`
    case 0:
      return `no speeding`
    default:
      return `-`
  }
}

type Props = {
  geoJsonFeature: Feature<LineString, LineStringFeatureProperties>
}

const locationDetails = (feature: Feature<LineString, LineStringFeatureProperties>) => {
  const { roadName, townName, regionName, postcode } = feature.properties || {}

  // Only render if gpsTimestamp is defined and not an empty string
  if (!regionName) return null

  // Build the first line: Region + (Town) if available
  let regionAndTown = ``
  if (regionName) {
    regionAndTown = townName ? `${regionName} (${townName})` : regionName
  } else if (townName) {
    regionAndTown = townName // No region, just town
  }

  // Build the second line: Road + (Postcode) if available
  let roadNameAndPostcode = ``
  if (roadName) {
    roadNameAndPostcode = postcode ? `${roadName} (${postcode})` : roadName
  } else if (postcode) {
    roadNameAndPostcode = `${postcode}` // No road, just postcode
  }

  return (
    <>
      <Text css={styles.label}>Location Details:</Text>
      {regionAndTown && <Text css={styles.stat}>{regionAndTown}</Text>}
      {roadNameAndPostcode && <Text css={styles.stat}>{roadNameAndPostcode}</Text>}
    </>
  )
}

export const MapFeatureTooltipContent = ({ geoJsonFeature }: Props) => {
  const metrics = getLineStringSpeedMetrics(geoJsonFeature)

  const speedingClass = speedingClassification(metrics)

  const timestamp =
    geoJsonFeature.properties.gpsTimestamp !== undefined
      ? cast(LocalDateTime)(geoJsonFeature.properties.gpsTimestamp)
      : undefined

  return (
    <Box css={styles.container}>
      <Text css={styles.label}>Speed Limit:</Text>
      <Text css={styles.stat}>{metrics.speedLimitMph ? `${Math.round(metrics.speedLimitMph)} MPH` : `Unknown`}</Text>
      <Text css={styles.label}>Average speed:</Text>
      <Flex>
        {metrics.speedMph && !metrics.hasUnreliableSpeed && (
          <Text css={styles.stat}>{`${Math.round(metrics.speedMph)} MPH`}</Text>
        )}
        <Text css={styles.speedingClass(speedingClass)}>
          {geoJsonFeature.properties.gpsTimestamp !== undefined
            ? generateAverageSpeedClassLabel(geoJsonFeature).toUpperCase()
            : generateAverageSpeedClassLabelOld(speedingClass, metrics).toUpperCase()}
        </Text>
      </Flex>
      {metrics.isSpeeding && !metrics.hasUnreliableSpeed && metrics.speedExceedancePercentage !== null ? (
        <>
          <Text css={styles.label}>Average speed exceedance:</Text>
          <Text css={styles.stat}>{`${metrics.speedExceedancePercentage.toFixed(0)}%`}</Text>
        </>
      ) : null}
      {locationDetails(geoJsonFeature)}
      {timestamp !== undefined && (
        <>
          <Text css={styles.label}>Timestamp:</Text>
          <Text css={styles.stat}>{localDateTimeToString(timestamp, `en-GB`, `HH:mm`)}</Text>
        </>
      )}
    </Box>
  )
}
